import { eLeadbotState } from '../pages/MessengerV2/Messenger.enum';
import { eUserRole } from '../providers/UserProvider/User.enums';

// BUG-892: the HTTP lists hide SESSION_ACTIVE leads for every role, so the Pipe
// board and the Messenger chat list must agree on when a socket update reveals a
// lead (session ended) or hides it (session started). Both hooks read these two
// predicates so the rule lives in one place.

/** Mirrors the server's held-before fallback when an update omits `original`. */
export const hasLeadLeftLeadbotSession = (options: {
  previousState: eLeadbotState | null | undefined;
  currentState: eLeadbotState | null | undefined;
  stateChanged: boolean;
}): boolean =>
  options.currentState !== eLeadbotState.SESSION_ACTIVE &&
  (options.previousState === eLeadbotState.SESSION_ACTIVE ||
    (options.previousState === undefined && options.stateChanged));

/**
 * Is the Leadbot holding this lead right now? A held lead belongs on no list.
 *
 * Level-triggered on purpose. This used to ask whether the lead had just *entered* a
 * session (`previous !== SESSION_ACTIVE && current === SESSION_ACTIVE`), which missed every
 * subsequent update while the hold persisted — the bot's own `changed_last_message_on`
 * follow-up arrives as `SESSION_ACTIVE -> SESSION_ACTIVE`. Neither that predicate nor
 * `hasLeadLeftLeadbotSession` matched it, so the generic update path treated the lead as
 * absent (this branch had just removed its card) and re-inserted it with the stage count one
 * high, while the HTTP lists kept hiding it — the "appears and vanishes" symptom BUG-892
 * exists to remove. Asking about the current state cannot miss a steady-hold update.
 */
export const isLeadHeldByLeadbot = (
  currentState: eLeadbotState | null | undefined,
): boolean => currentState === eLeadbotState.SESSION_ACTIVE;

/**
 * Is this viewer a workspace superuser? Mirrors the server's `is_superuser`, which is true
 * for OWNER and ADMIN and false for SELLER and VIEWER.
 */
export const isSuperuserRole = (
  role: eUserRole | string | null | undefined,
): boolean => role === eUserRole.OWNER || role === eUserRole.ADMIN;

/**
 * Does the Leadbot's hold hide this lead from *this* viewer?
 *
 * Mirrors `LeadEventAudience::reaches()` (leads-service), which checks `is_superuser`
 * **before** the hold: owners and admins keep receiving a held lead on purpose, because they
 * are the only actor who can reach one whose session never ends. Every other role loses it,
 * exactly as the HTTP lists hide it from them.
 *
 * The role arm is what stops the client from discarding an event the server had just decided
 * to deliver. Without it an admin saw the card appear at create and vanish on the bot's next
 * message, with the stage count left one high — the BUG-892 symptom, aimed at the people who
 * report it.
 */
export const hidesLeadFromViewer = (options: {
  currentState: eLeadbotState | null | undefined;
  currentUserRole: eUserRole | string | null | undefined;
}): boolean =>
  isLeadHeldByLeadbot(options.currentState) &&
  !isSuperuserRole(options.currentUserRole);
